import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BaseComponent } from '@macs-frontend/core';
import { environment as env } from '@macs-frontend/env';
import { State } from '@macs-frontend/reducers';
import { DragDropError } from '@macs-frontend/web-macs/shared';
import { Store } from '@ngrx/store';
import { takeUntil } from 'rxjs/operators';
import { CoreApiService } from '../../../../core/services/core-api.service';
import { DragDropDirective } from '../../directives/dragdrop.directive';

@Component({
  selector: 'md-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileUploadComponent),
      multi: true,
    },
  ],
})
export class FileUploadComponent
  extends BaseComponent
  implements ControlValueAccessor, OnInit, OnChanges
{
  // @ts-expect-error
  @Input() label: string;
  @Input() canUpload = true;
  @Input() canRead = true;
  @Input() accept = 'image/*';
  @Input() labelClasses = 'input-file input-file_200 active';
  @Input() fallbackImage: string | null = null;

  @Output()
  changeHandler: EventEmitter<File | null> = new EventEmitter<File | null>();
  @Output() readHandler: EventEmitter<string> = new EventEmitter<string>();
  @Output() uploadHandler: EventEmitter<string> = new EventEmitter<string>();
  @Output() removeHandler: EventEmitter<any> = new EventEmitter<any>();
  @Output()
  errorHandler: EventEmitter<DragDropError> = new EventEmitter<DragDropError>();

  get model() {
    return this.modelValue;
  }
  set model(value) {
    this.writeValue(value);
    this.onChangeCallback(value);
  }

  // @ts-expect-error
  imageSrc: string;
  imagesPath = env.images_path;

  // @ts-expect-error
  private modelValue: string;

  get hasOwnImage(): boolean {
    return !!this.modelValue;
  }

  onChangeCallback = (_: any) => {};

  constructor(
    private coreApiService: CoreApiService,
    readonly cdr: ChangeDetectorRef,
    readonly store$: Store<State>,
  ) {
    super();
  }

  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.fallbackImage && !this.modelValue) {
      this.updateImageSrc();
      this.cdr.markForCheck();
    }
  }

  setImageSrc(path: string) {
    return (this.imageSrc = path);
  }

  fileChange(input: HTMLInputElement, dragDropDirective: DragDropDirective) {
    // @ts-expect-error
    if (input.files.length) {
      // @ts-expect-error
      this.upload(input.files[0]);
      // @ts-expect-error
      this.changeHandler.emit(input.files[0]);
      // @ts-expect-error
      dragDropDirective.readFile(input.files[0]);
    }
    input.value = '';
  }

  fileDrop(file: File | null) {
    // @ts-expect-error
    this.upload(file);
    this.changeHandler.emit(file);
  }

  fileDropRead(src: string) {
    this.readHandler.emit(src);
    this.setImageSrc(src);
  }

  fileDropError(e: DragDropError) {
    this.errorHandler.emit(e);
  }

  removeImage() {
    // @ts-expect-error
    this.setImageSrc(null);
    // @ts-expect-error
    this.model = null;
    this.removeHandler.emit();
  }

  upload(file: File) {
    if (!this.canUpload) {
      return;
    }

    this.coreApiService
      .uploadFile(file)
      .pipe(takeUntil(this.destroy$))
      // @ts-expect-error
      .subscribe((filename: string) => {
        this.model = filename;
        this.uploadHandler.emit(filename);
      });
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {}

  setDisabledState(isDisabled: boolean): void {}

  writeValue(value: string): void {
    this.modelValue = value;
    this.updateImageSrc();
    this.cdr.markForCheck();
  }

  private updateImageSrc(): void {
    const image = this.modelValue || this.fallbackImage;
    this.setImageSrc(image ? this.imagesPath + image : '');
  }
}
